import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Plus, X } from 'lucide-react';
import { DAYS_OF_WEEK, formatDayName } from './constants';

// Step 2 of the dialog: weekly hours + per-day blocked periods.
export default function LocationFormHoursTab({
  formData, onUpdateOperatingHours, onAddBreak, onUpdateBreakField, onRemoveBreak,
}) {
  return (
    <div className="space-y-4 mt-4">
      <p className="text-sm text-muted-foreground mb-4">
        Set the operating hours and blocked periods for each day of the week.
      </p>

      {/* Friday Prayer Notice for Brunei */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
        <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
          🕌 Brunei Friday Prayer
        </p>
        <p className="text-sm text-amber-700 mt-1">
          By law in Brunei, all businesses are closed from 12:00 PM to 2:00 PM on Fridays for prayer.
          This break is automatically set for Friday.
        </p>
      </div>

      {DAYS_OF_WEEK.map((day) => {
        const dayHours = formData.operating_hours[day] || {};
        const breaks = dayHours.breaks || [];

        return (
          <div key={day} className="p-4 bg-muted/30 rounded-xl">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-28 font-bold">{formatDayName(day)}</div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!dayHours.closed}
                  onCheckedChange={(checked) => onUpdateOperatingHours(day, 'closed', !checked)}
                />
                <span className="text-sm text-muted-foreground">
                  {dayHours.closed ? 'Closed' : 'Open'}
                </span>
              </div>
              {!dayHours.closed && (
                <>
                  <Input
                    type="time"
                    value={dayHours.open || '08:00'}
                    onChange={(e) => onUpdateOperatingHours(day, 'open', e.target.value)}
                    className="w-32 h-10 rounded-xl"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={dayHours.close || '22:00'}
                    onChange={(e) => onUpdateOperatingHours(day, 'close', e.target.value)}
                    className="w-32 h-10 rounded-xl"
                  />
                </>
              )}
            </div>

            {!dayHours.closed && (
              <div className="mt-3 pt-3 border-t border-muted">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Blocked Hours</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => onAddBreak(day)}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Break
                  </Button>
                </div>

                {breaks.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No blocked hours</p>
                ) : (
                  <div className="space-y-2">
                    {breaks.map((breakPeriod, breakIdx) => (
                      <div
                        key={breakPeriod._uid || `brk-${breakIdx}`}
                        className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg"
                      >
                        <Input
                          type="time"
                          value={breakPeriod.start || '12:00'}
                          onChange={(e) => onUpdateBreakField(day, breakIdx, 'start', e.target.value)}
                          className="w-28 h-8 text-sm rounded-lg"
                        />
                        <span className="text-muted-foreground">-</span>
                        <Input
                          type="time"
                          value={breakPeriod.end || '14:00'}
                          onChange={(e) => onUpdateBreakField(day, breakIdx, 'end', e.target.value)}
                          className="w-28 h-8 text-sm rounded-lg"
                        />
                        <Input
                          placeholder="Reason (e.g., Friday Prayer)"
                          value={breakPeriod.reason || ''}
                          onChange={(e) => onUpdateBreakField(day, breakIdx, 'reason', e.target.value)}
                          className="flex-1 h-8 text-sm rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => onRemoveBreak(day, breakIdx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
